import { Link } from "react-router-dom";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines } from "../components/Motion";
import { CtaBand } from "../components/CtaBand";
import { useLang } from "../i18n/LanguageContext";
import { SITE_URL, LOGO_URL } from "../data/content";

const schema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  url: `${SITE_URL}/about`,
  mainEntity: {
    "@type": "Person",
    name: "Jancie Santiago González",
    jobTitle: "Founder & Certified Medical Interpreter (CMI)",
    alumniOf: [
      { "@type": "CollegeOrUniversity", name: "Meredith College" },
      { "@type": "CollegeOrUniversity", name: "La Salle University" }
    ],
    knowsLanguage: ["en", "es"],
    worksFor: { "@type": "Organization", name: "SG Language Services LLC", logo: LOGO_URL }
  }
};

const ABOUT_IMG = "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export default function About() {
  const { t } = useLang();
  const a = t.about;
  return (
    <>
      <Seo title={a.seoTitle} description={a.seoDesc} path="/about" schema={schema} />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-24 texture-grain" data-testid="about-page">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-periwinkle-deep mb-6">{a.kicker}</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.0] tracking-tight max-w-4xl">
          <MaskedLines lines={a.heroLines} />
        </h1>

        <div className="mt-20 grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5">
            <div className="rounded-t-full overflow-hidden border-8 border-white shadow-2xl shadow-periwinkle/20">
              <img src={ABOUT_IMG} alt={a.imgAlt} className="w-full h-[30rem] object-cover" />
            </div>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-muted2 text-center">{a.caption}</p>
          </Reveal>

          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold leading-tight">{a.h2}</h2>
              {a.paras.map((p, i) => (
                <p key={i} className="mt-6 text-muted2 leading-relaxed">{p}</p>
              ))}
            </Reveal>

            <div className="mt-12 grid sm:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
              {a.credentials.map((c, i) => (
                <Reveal key={c.k} delay={i * 0.08} className="bg-cream p-8">
                  <p className="font-serif text-3xl font-semibold text-periwinkle-deep">{c.k}</p>
                  <p className="text-sm text-muted2 leading-relaxed mt-2">{c.v}</p>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.2}>
              <Link to="/contact" data-testid="about-cta-btn" className="inline-block mt-12 rounded-full bg-ink text-cream px-8 py-4 font-semibold hover:bg-periwinkle-deep transition-colors duration-300">
                {a.cta}
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
